import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Bot, Loader2, Search, Download, X, ShieldAlert, BookOpen, Trash2,
  Upload, MessageSquare, Clock, FileText, Eye,
} from "lucide-react";
import api from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";

const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const fmtKb = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/* ─── Modale trascrizione conversazione ─── */
const TranscriptModal = ({ conv, onClose }) => {
  const [msgs, setMsgs] = useState(null);
  useEffect(() => {
    api.get(`/ai/conversations/${conv.id}/messages`)
      .then(({ data }) => setMsgs(data.items || []))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Errore caricamento messaggi"); setMsgs([]); });
  }, [conv.id]);

  return (
    <div className="wl-modal-overlay" onClick={onClose}>
      <div className="wl-modal wl-modal--md" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}
           data-testid="ai-transcript-modal">
        <div className="wl-modal__header">
          <div className="wl-modal__icon wl-modal__icon--info" aria-hidden="true"><MessageSquare size={16} /></div>
          <div className="wl-modal__title-block">
            <h3 className="wl-modal__title">{conv.title}</h3>
            <p className="wl-modal__desc">{conv.user_email} · {fmtDt(conv.created_at)}</p>
          </div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary" aria-label="Chiudi"><X size={16} /></button>
        </div>
        <div className="wl-modal__body space-y-2" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {msgs === null ? (
            <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin" style={{ color: "var(--wl-text-muted)" }} /></div>
          ) : msgs.length === 0 ? (
            <div className="text-sm text-center py-6" style={{ color: "var(--wl-text-secondary)" }}>Nessun messaggio.</div>
          ) : (
            msgs.map((m) => (
              <div key={m.id} className={`rounded-lg border px-3 py-2 ${m.role === "user" ? "border-accent/30 bg-accent-subtle/40" : "border-border bg-subtle/50"}`}>
                <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-wider" style={{ color: "var(--wl-text-muted)" }}>
                  <span className="font-semibold">{m.role === "user" ? "Utente" : "AI"}</span>
                  <span>{fmtDt(m.created_at)}</span>
                  {(m.sensitive_warning_types || []).length > 0 && (
                    <span className="inline-flex items-center gap-1 normal-case font-medium" style={{ color: "var(--wl-warning, #b45309)" }}
                          data-testid="transcript-sensitive-badge">
                      <ShieldAlert size={10} /> Avviso dati sensibili: {m.sensitive_warning_types.join(", ")}
                    </span>
                  )}
                </div>
                <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "var(--wl-ink)" }}>{m.content}</div>
                {(m.kb_sources || []).length > 0 && (
                  <div className="mt-1.5 text-[10px] inline-flex items-center gap-1" style={{ color: "var(--wl-text-muted)" }}>
                    <BookOpen size={10} /> {m.kb_sources.map((s) => s.title).join(" · ")}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Card: Modelli AI assegnati (admin) ─── */
const ModelsCard = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/ai/admin/settings").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.put("/ai/admin/settings", payload);
      setCfg((c) => ({ ...c, ...data }));
      toast.success("Modelli aggiornati");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => {
    const enabled = cfg.models_enabled.includes(key)
      ? cfg.models_enabled.filter((k) => k !== key)
      : [...cfg.models_enabled, key];
    if (enabled.length === 0) {
      toast.error("Almeno un modello deve restare attivo.");
      return;
    }
    save({ models_enabled: enabled });
  };

  if (!cfg) return null;
  return (
    <div className="bg-surface border border-border rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap"
         data-testid="ai-models-card">
      <div className="flex items-center gap-2.5 min-w-0">
        <Bot size={16} className="flex-shrink-0" style={{ color: "var(--wl-text-muted)" }} />
        <div>
          <div className="text-sm font-medium" style={{ color: "var(--wl-ink)" }}>Modelli AI per il tuo team</div>
          <div className="text-xs" style={{ color: "var(--wl-text-secondary)" }}>
            Scegli quali modelli possono usare i tuoi utenti e quale proporre di default.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {(cfg.available_models || []).map((m) => {
          const on = cfg.models_enabled.includes(m.key);
          const isDefault = cfg.default_model === m.key;
          return (
            <div key={m.key} className={`flex items-center gap-1.5 rounded-full border px-1 py-1 ${on ? "border-accent bg-accent-subtle" : "border-border"}`}>
              <button type="button" disabled={saving} onClick={() => toggle(m.key)}
                      className="text-xs px-2 py-0.5 font-medium"
                      style={{ color: on ? "var(--wl-ink)" : "var(--wl-text-muted)" }}
                      data-testid={`ai-model-toggle-${m.key}`}>
                {m.label} {on ? "· attivo" : "· spento"}
              </button>
              {on && (
                <button type="button" disabled={saving || isDefault}
                        onClick={() => save({ default_model: m.key })}
                        className={`text-[10px] px-2 py-0.5 rounded-full ${isDefault ? "font-semibold" : "hover:underline"}`}
                        style={{ background: isDefault ? "var(--wl-brand-yellow, #F4F430)" : "transparent",
                                 color: isDefault ? "#16161A" : "var(--wl-text-secondary)" }}
                        data-testid={`ai-model-default-${m.key}`}>
                  {isDefault ? "default" : "usa come default"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Tab: Uso dell'AI ─── */
const UsageTab = () => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [retention, setRetention] = useState(6);
  const [savingRet, setSavingRet] = useState(false);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (search.trim()) p.set("search", search.trim());
    if (userId) p.set("user_id", userId);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    return p.toString();
  }, [search, userId, dateFrom, dateTo]);

  const [usageFailed, setUsageFailed] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/ai/admin/usage?${params()}`);
      setData(d);
      setUsageFailed(false);
      if (d.retention_months) setRetention(d.retention_months);
    } catch (e) {
      setUsageFailed(true);
      setData((prev) => prev || { items: [], users: [], totals: {} });
    }
  }, [params]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveRetention = async (months) => {
    setSavingRet(true);
    try {
      const { data: d } = await api.put("/ai/admin/settings", { retention_months: months });
      setRetention(d.retention_months);
      toast.success(`Conservazione impostata a ${d.retention_months} mesi${d.purged ? ` · ${d.purged} conversazioni eliminate` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingRet(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/ai/admin/usage/export?${params()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : "Uso_AI.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Export Excel scaricato");
    } catch (e) {
      toast.error("Errore nell'export");
    } finally {
      setExporting(false);
    }
  };

  const totals = data?.totals || {};

  return (
    <div className="space-y-4">
      {/* Modelli AI assegnati */}
      <ModelsCard />

      {/* Retention */}
      <div className="bg-surface border border-border rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap"
           data-testid="ai-retention-card">
        <div className="flex items-center gap-2.5 min-w-0">
          <Clock size={16} className="flex-shrink-0" style={{ color: "var(--wl-text-muted)" }} />
          <div>
            <div className="text-sm font-medium" style={{ color: "var(--wl-ink)" }}>Conservazione conversazioni</div>
            <div className="text-xs" style={{ color: "var(--wl-text-secondary)" }}>
              Oltre la soglia le conversazioni vengono <strong>eliminate definitivamente</strong> (utenti e amministratori).
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {[6, 12, 24].map((m) => (
            <button key={m} type="button" disabled={savingRet} onClick={() => saveRetention(m)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${retention === m ? "border-accent bg-accent-subtle font-medium" : "border-border hover:border-accent"}`}
                    style={{ color: retention === m ? "var(--wl-ink)" : "var(--wl-text-secondary)" }}
                    data-testid={`retention-${m}`}>
              {m} mesi
            </button>
          ))}
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-surface border border-border rounded-xl p-4">
        <form className="flex items-end gap-3 flex-wrap" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <div className="wl-field flex-1 min-w-[200px]">
            <label className="wl-label">Ricerca nel testo</label>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--wl-text-muted)" }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Parola nei messaggi o nel titolo…"
                     className="wl-input" style={{ paddingLeft: "30px" }} data-testid="ai-usage-search" />
            </div>
          </div>
          <div className="wl-field">
            <label className="wl-label">Utente</label>
            <select value={userId} onChange={(e) => setUserId(e.target.value)} className="wl-input"
                    data-testid="ai-usage-user-filter">
              <option value="">Tutti</option>
              {(data?.users || []).map((u) => <option key={u.user_id} value={u.user_id}>{u.email}</option>)}
            </select>
          </div>
          <div className="wl-field">
            <label className="wl-label">Dal</label>
            <DateInputIT value={dateFrom} onChange={setDateFrom} testId="ai-usage-date-from" />
          </div>
          <div className="wl-field">
            <label className="wl-label">Al</label>
            <DateInputIT value={dateTo} min={dateFrom} onChange={setDateTo} testId="ai-usage-date-to" />
          </div>
          <button type="submit" className="wl-btn wl-btn--ghost" style={{ border: "1px solid var(--wl-border)" }}
                  data-testid="ai-usage-apply">Filtra</button>
          <button type="button" onClick={exportExcel} disabled={exporting || !data}
                  className="wl-btn wl-btn--primary" data-testid="ai-usage-export">
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Esporta Excel
          </button>
        </form>
        <div className="flex items-center gap-4 mt-3 text-xs" style={{ color: "var(--wl-text-secondary)" }} data-testid="ai-usage-totals">
          <span><strong style={{ color: "var(--wl-ink)" }}>{totals.conversations ?? "—"}</strong> conversazioni</span>
          <span><strong style={{ color: "var(--wl-ink)" }}>{totals.messages ?? "—"}</strong> messaggi</span>
          <span className="inline-flex items-center gap-1">
            <ShieldAlert size={12} style={{ color: "var(--wl-warning, #b45309)" }} />
            <strong style={{ color: "var(--wl-ink)" }}>{totals.sensitive_warnings ?? "—"}</strong> avvisi dati sensibili
          </span>
        </div>
      </div>

      {/* Tabella conversazioni */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        {data === null ? (
          <div className="flex justify-center py-10"><Loader2 size={18} className="animate-spin" style={{ color: "var(--wl-text-muted)" }} /></div>
        ) : usageFailed ? (
          <LoadErrorBox what="le conversazioni" onRetry={load} testId="ai-usage-error" />
        ) : (data.items || []).length === 0 ? (
          <div className="text-sm text-center py-10" style={{ color: "var(--wl-text-secondary)" }} data-testid="ai-usage-empty">
            Nessuna conversazione trovata con questi filtri.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider border-b border-border" style={{ color: "var(--wl-text-muted)" }}>
                  <th className="px-4 py-2.5 font-medium">Data</th>
                  <th className="px-4 py-2.5 font-medium">Utente</th>
                  <th className="px-4 py-2.5 font-medium">Conversazione</th>
                  <th className="px-4 py-2.5 font-medium text-center">Msg</th>
                  <th className="px-4 py-2.5 font-medium text-center">Avvisi dati</th>
                  <th className="px-4 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id} className="border-b border-border last:border-0 hover:bg-subtle/60 cursor-pointer transition-colors"
                      onClick={() => setOpen(c)} data-testid={`ai-usage-row-${c.id}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap text-xs" style={{ color: "var(--wl-text-secondary)" }}>{fmtDt(c.last_message_at)}</td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: "var(--wl-ink)" }}>{c.user_email}</td>
                    <td className="px-4 py-2.5 max-w-[320px]">
                      <span className="block truncate text-sm" style={{ color: "var(--wl-ink)" }}>{c.title}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center text-xs" style={{ color: "var(--wl-text-secondary)" }}>{c.message_count}</td>
                    <td className="px-4 py-2.5 text-center">
                      {(c.sensitive_warnings || 0) > 0 ? (
                        <span className="wl-badge wl-badge--warning" style={{ fontSize: "10px" }} data-testid={`sensitive-badge-${c.id}`}>
                          <ShieldAlert size={10} /> {c.sensitive_warnings}
                        </span>
                      ) : (
                        <span className="text-xs" style={{ color: "var(--wl-text-muted)" }}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Eye size={14} style={{ color: "var(--wl-text-muted)" }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && <TranscriptModal conv={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

/* ─── Tab: Memoria aziendale (base documentale) ─── */
const KbTab = () => {
  const [docs, setDocs] = useState(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);

  const [kbFailed, setKbFailed] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/admin/kb");
      setDocs(data.items || []);
      setKbFailed(false);
    } catch (e) {
      setKbFailed(true);
      setDocs((d) => d || []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file (PDF, DOCX, TXT o MD)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      await api.post("/ai/admin/kb", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento aggiunto alla base di conoscenza");
      setFile(null); setTitle("");
      document.getElementById("kb-file-input").value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento del documento");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.title}" dalla base di conoscenza?`)) return;
    try {
      await api.delete(`/ai/admin/kb/${doc.id}`);
      toast.success("Documento eliminato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-surface border border-border rounded-xl p-4" data-testid="kb-upload-card">
        <div className="text-sm font-medium mb-1" style={{ color: "var(--wl-ink)" }}>Carica un documento aziendale</div>
        <p className="text-xs mb-3" style={{ color: "var(--wl-text-secondary)" }}>
          Procedure, regolamenti, manuali, glossari: l&apos;AI risponde basandosi su questi documenti e cita la fonte.
          Il modello <strong>non viene addestrato</strong>: è una base documentale consultata a ogni domanda.
        </p>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="wl-field flex-1 min-w-[220px]">
            <label className="wl-label">File (PDF, DOCX, TXT, MD — max 10 MB)</label>
            <input id="kb-file-input" type="file" accept=".pdf,.docx,.txt,.md" disabled={uploading}
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   className="wl-input" style={{ padding: "8px 12px", height: "auto" }}
                   data-testid="kb-file-input" />
          </div>
          <div className="wl-field flex-1 min-w-[180px]">
            <label className="wl-label">Titolo (opzionale)</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                   placeholder="es. Regolamento ferie e permessi" className="wl-input" data-testid="kb-title-input" />
          </div>
          <button type="button" onClick={upload} disabled={uploading || !file}
                  className="wl-btn wl-btn--primary" data-testid="kb-upload-submit">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Carica
          </button>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        {docs === null ? (
          <div className="flex justify-center py-10"><Loader2 size={18} className="animate-spin" style={{ color: "var(--wl-text-muted)" }} /></div>
        ) : kbFailed ? (
          <LoadErrorBox what="i documenti della base di conoscenza" onRetry={load} testId="kb-load-error" />
        ) : docs.length === 0 ? (
          <div className="text-center py-10 px-4" data-testid="kb-empty">
            <BookOpen size={26} strokeWidth={1.5} className="mx-auto mb-2" style={{ color: "var(--wl-text-muted)" }} />
            <div className="text-sm" style={{ color: "var(--wl-text-secondary)" }}>
              Nessun documento ancora. Carica il primo: l&apos;AI inizierà a rispondere anche sulle vostre procedure interne.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider border-b border-border" style={{ color: "var(--wl-text-muted)" }}>
                  <th className="px-4 py-2.5 font-medium">Documento</th>
                  <th className="px-4 py-2.5 font-medium">File</th>
                  <th className="px-4 py-2.5 font-medium text-center">Dimensione</th>
                  <th className="px-4 py-2.5 font-medium">Caricato da</th>
                  <th className="px-4 py-2.5 font-medium">Data</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="border-b border-border last:border-0" data-testid={`kb-doc-${d.id}`}>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: "var(--wl-ink)" }}>
                        <FileText size={13} className="flex-shrink-0" style={{ color: "var(--wl-text-muted)" }} /> {d.title}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: "var(--wl-text-secondary)" }}>{d.filename}</td>
                    <td className="px-4 py-2.5 text-center text-xs" style={{ color: "var(--wl-text-secondary)" }}>{fmtKb(d.size_bytes)}</td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: "var(--wl-text-secondary)" }}>{d.uploaded_by}</td>
                    <td className="px-4 py-2.5 text-xs whitespace-nowrap" style={{ color: "var(--wl-text-secondary)" }}>{fmtDt(d.uploaded_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button type="button" onClick={() => remove(d)}
                              className="p-1.5 rounded hover:bg-subtle transition-colors" title="Elimina"
                              data-testid={`kb-delete-${d.id}`}>
                        <Trash2 size={13} style={{ color: "var(--wl-danger, #b91c1c)" }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Pagina ─── */
export default function AiUsage() {
  const [tab, setTab] = useState("usage");
  return (
    <div className="wl-page" data-testid="admin-ai-page">
      <div className="wl-page__header" style={{ marginBottom: "16px" }}>
        <div className="wl-page__eyebrow">
          <Bot size={12} strokeWidth={2} style={{ marginRight: 4, verticalAlign: "-1px" }} />
          AI aziendale
        </div>
        <h1 className="wl-page__title">Uso dell&apos;AI</h1>
        <p className="wl-page__meta">
          Le conversazioni del tuo team con l&apos;AI aziendale: storico consultabile, conservazione configurabile e base documentale.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button type="button" onClick={() => setTab("usage")}
                className={`text-sm px-4 py-2 rounded-lg border transition-colors ${tab === "usage" ? "border-accent bg-accent-subtle font-medium" : "border-border hover:border-accent"}`}
                style={{ color: tab === "usage" ? "var(--wl-ink)" : "var(--wl-text-secondary)" }}
                data-testid="tab-usage">
          <MessageSquare size={13} style={{ display: "inline", verticalAlign: "-2px", marginRight: 6 }} />
          Conversazioni
        </button>
        <button type="button" onClick={() => setTab("kb")}
                className={`text-sm px-4 py-2 rounded-lg border transition-colors ${tab === "kb" ? "border-accent bg-accent-subtle font-medium" : "border-border hover:border-accent"}`}
                style={{ color: tab === "kb" ? "var(--wl-ink)" : "var(--wl-text-secondary)" }}
                data-testid="tab-kb">
          <BookOpen size={13} style={{ display: "inline", verticalAlign: "-2px", marginRight: 6 }} />
          Memoria aziendale
        </button>
      </div>

      {tab === "usage" ? <UsageTab /> : <KbTab />}
    </div>
  );
}
